import React, { useState, useEffect } from 'react';
import {
  Accordion, Table, Badge, Alert, Form, Row, Col, Button, InputGroup
} from 'react-bootstrap';
import { FaCheck } from 'react-icons/fa';
import axios from 'axios';

const CCPARetentionTab = ({ userData, onScoreUpdate, showNotification, region, refreshActivities,logToAccessLogs }) => {
  const [retentionPolicies, setRetentionPolicies] = useState([]);
  const [upcomingDeletions, setUpcomingDeletions] = useState([]);
  const [newPolicy, setNewPolicy] = useState({
    category: '',
    periodValue: '',
    periodUnit: 'Years',
    businessPurpose: '',
    autoDelete: false
  });

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'] 
  };

const logActivity = async (message, badge = "info") => {
 
  
  try {
    // ✅ Map frontend fields to backend entity fields
    const payload = {
      action: badge,                    // badge → action
      module: "Data Retention",              // Add module field
      description: message                  // message → details
      // userId and timestamp set by backend
    };
    
    
    
    const response = await axios.post(
      "https://wjioewyrif.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/activities/create",  // ✅ Correct endpoint
      payload,     
      { headers }  // ✅ Send headers properly
    );
    
    
    if (refreshActivities) {
        await refreshActivities();
      }


  } catch (err) {
    console.error('❌ Error logging inventory activity:', err);
    
    if (err.response) {
      console.error('🔥 Server Error Details:');
      console.error('Status:', err.response.status);
      console.error('Data:', err.response.data);
    }
    
    console.warn("Failed to log activity:", err.message);
  }
};

 useEffect(() => {
  if (userData?.['cognito:username']) {
    fetchPolicies();
  }
}, [userData]);

  const fetchPolicies = async () => {
    try {
      const policyRes = await axios.get('https://qrubzcerac.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/retention-policy/list', { headers });
      setRetentionPolicies(policyRes.data);

      const deletionRes = await axios.get('https://3m3xvpg2wh.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/retention-policy/scheduled-deletions', { headers });
      setUpcomingDeletions(deletionRes.data);

      // CCPA-specific scoring - focuses on consumer control and business purpose
      const MIN_CATEGORIES = [
        'Personal Information',
        'Sensitive Personal Information',
        'Commercial Information',
        'Internet Activity',
        'Customer Communications'
      ];
      const MAX_RETENTION_YEARS = 5; // CCPA encourages shorter retention for consumer privacy

      const totalPolicies = policyRes.data.length;
      const autoDeleteCount = policyRes.data.filter(p => p.autoDelete).length;
      const fullyConfigured = policyRes.data.filter(
        p => p.category && p.businessPurpose && p.periodValue > 0
      ).length;
      const overdueRetention = policyRes.data.filter(
        p => p.periodUnit === 'Years' && p.periodValue > MAX_RETENTION_YEARS
      ).length;
      const missingCategories = MIN_CATEGORIES.filter(
        cat => !policyRes.data.some(p => p.category === cat)
      ).length;

      let score = 0;
      if (totalPolicies === 0) {
        score = 0;
      } else {
        score = 100;
        score -= missingCategories * 15; // -15 per missing critical category
        score -= overdueRetention * 25; // -25 per overdue retention (stricter for CCPA consumer rights)
        score -= (totalPolicies - fullyConfigured) * 10; // -10 per incomplete policy
        score = Math.max(0, Math.min(100, score));
      }

     if (onScoreUpdate) {
  onScoreUpdate(Math.round(score));
}

    } catch (error) {
      console.error('Error fetching policies or deletions', error);
    }
  };

  const handleNewPolicyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewPolicy((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePolicySubmit = async (e) => {
    e.preventDefault();
    const { category, periodValue, periodUnit, businessPurpose, autoDelete } = newPolicy;
    const numVal = Number(periodValue);

    if (!category || !numVal || numVal <= 0 || !businessPurpose) {
      showNotification("Please fill all required fields", "danger");
      return;
    }

    const payload = {
      category: category.trim(),
      periodValue: numVal,
      periodUnit,
      businessPurpose,
      autoDelete
    };

    try {
      await axios.post('https://tvcyxpk47b.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/retention-policy/create', payload, { headers });
      await logActivity(`Created CCPA retention policy for ${payload.category}`, "success");
      logToAccessLogs?.({
        action:      'Retention Policy Created',
        severity:    'success',
        description: `Created CCPA retention policy for ${payload.category} — ${payload.periodValue} ${payload.periodUnit}`,});
      await fetchPolicies();
      showNotification('Retention policy created successfully!');
      
      setNewPolicy({
        category: '',
        periodValue: '',
        periodUnit: 'Years',
        businessPurpose: '',
        autoDelete: false
      });
    } catch (error) {
      console.error('Error saving policy:', error);
      showNotification('Error saving policy', 'danger');
    }
  };

const [activeAccordionKey, setActiveAccordionKey] = useState(null);
  return (
    <>
       <div className="border rounded">
  <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "0" ? null : "0")
    }
  >
    <h6>Active Retention Policies</h6>
    <span>{activeAccordionKey === "0" ? "▲" : "▼"}</span>
  </div>
  {activeAccordionKey === "0" && (
    <div className="p-3 border-top">
            <Alert variant="info">
              <strong>CCPA Focus:</strong> Retention policies must align with stated business purposes and support 
              consumers' right to delete personal information[21][25].
            </Alert>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Data Category</th>
                  <th>Retention Period</th>
                  <th>Business Purpose</th>
                  <th>Auto-Delete</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {retentionPolicies.map((policy, index) => (
                  <tr key={index}>
                    <td>{policy.category}</td>
                    <td>{policy.periodValue} {policy.periodUnit}</td>
                    <td>{policy.businessPurpose}</td>
                    <td>{policy.autoDelete ? <FaCheck className="text-success" /> : '—'}</td>
                    <td>
                      <Badge bg="success">{policy.status || 'Active'}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          )}
          

       <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "1" ? null : "1")
    }
  >
    <h6>Scheduled Deletions</h6>
    <span>{activeAccordionKey === "1" ? "▲" : "▼"}</span>
  </div>
  {activeAccordionKey === "1" && (
    <div className="p-3 border-top">
            <Alert variant="info">
              <strong>Upcoming Deletions:</strong>
              {upcomingDeletions.length === 0 ? (
                <div>No scheduled deletions found.</div>
              ) : (
                upcomingDeletions.map((item, index) => (
                  <div key={index}>
                    {item.category} → Deletion on {item.nextDeletionDate}
                  </div>
                ))
              )}
            </Alert>
            </div>
  )}

          

        <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "2" ? null : "2")
    }
  >
    <h6>Create New Policy</h6>
    <span>{activeAccordionKey === "2" ? "▲" : "▼"}</span>
  </div>
  {activeAccordionKey === "2" && (
    <div className="p-3 border-top">
            <Form onSubmit={handlePolicySubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Data Category</Form.Label>
                    <Form.Control
                      type="text"
                      name="category"
                      value={newPolicy.category}
                      onChange={handleNewPolicyChange}
                      placeholder="e.g., Personal Information"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Retention Period</Form.Label>
                    <Form.Control
                      type="number"
                      name="periodValue"
                      value={newPolicy.periodValue}
                      onChange={handleNewPolicyChange}
                      min="1"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>&nbsp;</Form.Label>
                    <Form.Select
                      name="periodUnit"
                      value={newPolicy.periodUnit}
                      onChange={handleNewPolicyChange}
                    >
                      <option value="Days">Days</option>
                      <option value="Months">Months</option>
                      <option value="Years">Years</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Business Purpose for Retention</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="businessPurpose"
                  value={newPolicy.businessPurpose}
                  onChange={handleNewPolicyChange}
                  placeholder="e.g., Customer service support and product improvement"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="autoDelete"
                  checked={newPolicy.autoDelete}
                  onChange={handleNewPolicyChange}
                  label="Enable automatic deletion when retention period expires"
                />
              </Form.Group>
              <Button type="submit" variant="primary">Create Policy</Button>
            </Form>
          
        </div>
  )}
  </div>
    </>
  );
};

export default CCPARetentionTab;

